import { useCallback } from 'react';
import { BlockStack, InlineStack, Select, TextField, Button, Text, Box } from '@/lib/polaris';

/**
 * Builder de reglas para colecciones inteligentes (smart). Emite el jsonb que
 * consume el storefront worker (`parseSmartRules` / `productMatchesRules`):
 *   { match: 'all' | 'any', conditions: [{ field, op, value }] }
 *
 * match-all sin condiciones ⇒ TODOS los productos (semántica "Página de inicio").
 */
export type SmartOp =
  | 'eq' | 'neq'
  | 'gt' | 'lt' | 'gte' | 'lte'
  | 'contains' | 'starts_with' | 'ends_with'
  | 'on_sale' | 'not_on_sale';

export interface SmartCondition {
  field: string;
  op: SmartOp;
  value?: string | number | boolean;
}

export interface SmartRules {
  match: 'all' | 'any';
  conditions: SmartCondition[];
}

type FieldType = 'text' | 'number' | 'boolean' | 'sale';

const FIELDS: { value: string; label: string; type: FieldType }[] = [
  { value: 'title', label: 'Título', type: 'text' },
  { value: 'price', label: 'Precio', type: 'number' },
  { value: 'compare_at_price', label: 'Precio comparado', type: 'number' },
  { value: 'category', label: 'Categoría', type: 'text' },
  { value: 'vendor', label: 'Proveedor', type: 'text' },
  { value: 'tag', label: 'Etiqueta', type: 'text' },
  { value: 'sku', label: 'SKU', type: 'text' },
  { value: 'available', label: 'Disponible', type: 'boolean' },
  { value: 'on_sale', label: 'En oferta', type: 'sale' },
];

const OP_LABELS: Record<SmartOp, string> = {
  eq: 'es igual a',
  neq: 'no es',
  gt: 'mayor que',
  lt: 'menor que',
  gte: 'mayor o igual que',
  lte: 'menor o igual que',
  contains: 'contiene',
  starts_with: 'empieza con',
  ends_with: 'termina con',
  on_sale: 'en oferta',
  not_on_sale: 'sin oferta',
};

const OPS_BY_TYPE: Record<FieldType, SmartOp[]> = {
  text: ['eq', 'contains', 'starts_with', 'ends_with', 'neq'],
  number: ['eq', 'gt', 'lt', 'gte', 'lte'],
  boolean: ['eq'],
  sale: ['on_sale', 'not_on_sale'],
};

function fieldType(field: string): FieldType {
  return FIELDS.find((f) => f.value === field)?.type ?? 'text';
}

interface SmartCollectionRulesBuilderProps {
  value: SmartRules | null;
  onChange: (rules: SmartRules) => void;
}

export function SmartCollectionRulesBuilder({ value, onChange }: SmartCollectionRulesBuilderProps) {
  const rules: SmartRules = value ?? { match: 'all', conditions: [] };

  const update = useCallback(
    (next: SmartRules) => onChange(next),
    [onChange],
  );

  const setMatch = (match: string) => update({ ...rules, match: match === 'any' ? 'any' : 'all' });

  const addCondition = () =>
    update({ ...rules, conditions: [...rules.conditions, { field: 'title', op: 'contains', value: '' }] });

  const removeCondition = (idx: number) =>
    update({ ...rules, conditions: rules.conditions.filter((_, i) => i !== idx) });

  const patchCondition = (idx: number, patch: Partial<SmartCondition>) =>
    update({
      ...rules,
      conditions: rules.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });

  const onFieldChange = (idx: number, field: string) => {
    const ops = OPS_BY_TYPE[fieldType(field)];
    patchCondition(idx, { field, op: ops[0], value: fieldType(field) === 'boolean' ? true : '' });
  };

  return (
    <BlockStack gap="300">
      <Select
        label="Los productos deben cumplir"
        options={[
          { label: 'todas las condiciones', value: 'all' },
          { label: 'cualquier condición', value: 'any' },
        ]}
        value={rules.match}
        onChange={setMatch}
      />

      <BlockStack gap="300">
        {rules.conditions.map((cond, idx) => {
          const type = fieldType(cond.field);
          return (
            <InlineStack key={idx} gap="200" align="start" blockAlign="end" wrap>
              <Box minWidth="150px">
                <Select
                  label="Campo"
                  labelHidden
                  options={FIELDS.map((f) => ({ label: f.label, value: f.value }))}
                  value={cond.field}
                  onChange={(v) => onFieldChange(idx, v)}
                />
              </Box>
              {type !== 'sale' && (
                <Box minWidth="140px">
                  <Select
                    label="Operador"
                    labelHidden
                    options={OPS_BY_TYPE[type].map((op) => ({ label: OP_LABELS[op], value: op }))}
                    value={cond.op}
                    onChange={(v) => patchCondition(idx, { op: v as SmartOp })}
                  />
                </Box>
              )}
              {type === 'boolean' ? (
                <Box minWidth="120px">
                  <Select
                    label="Valor"
                    labelHidden
                    options={[
                      { label: 'Sí', value: 'true' },
                      { label: 'No', value: 'false' },
                    ]}
                    value={cond.value === false ? 'false' : 'true'}
                    onChange={(v) => patchCondition(idx, { value: v === 'true' })}
                  />
                </Box>
              ) : type !== 'sale' ? (
                <Box minWidth="160px">
                  <TextField
                    label="Valor"
                    labelHidden
                    type={type === 'number' ? 'number' : 'text'}
                    value={cond.value == null ? '' : String(cond.value)}
                    onChange={(v) => patchCondition(idx, { value: type === 'number' ? Number(v) : v })}
                    autoComplete="off"
                  />
                </Box>
              ) : null}
              <Button variant="tertiary" tone="critical" onClick={() => removeCondition(idx)}>
                Quitar
              </Button>
            </InlineStack>
          );
        })}
      </BlockStack>

      <InlineStack gap="200" blockAlign="center">
        <Button onClick={addCondition}>Agregar condición</Button>
        {rules.conditions.length === 0 && (
          <Text as="span" variant="bodySm" tone="subdued">
            Sin condiciones = todos los productos (como la “Página de inicio” de Shopify).
          </Text>
        )}
      </InlineStack>
    </BlockStack>
  );
}
